/**
 * @author Fernando Ruano
 * @file saga.js
 * @description User Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import {
  setResponse,
  setUser,
  setLoading
} from '@nephro/redux/stores/app/actions';

// Services
import userService from '@nephro/services/user-service';

// Constants
import {
  CREATE_USER,
  DELETE_USER_BY_ID,
  GET_ALL_USERS,
  GET_USER_BY_ID,
  UPDATE_USER,
  SET_USER_INFO,
  GET_USER_LOGIN_INFO,
  GET_USER_BY_USERNAME,
  GET_VALIDATE_PERMISSION,
  GET_USER_BY_EMAIL,
  GET_USER_DATA_REGISTER
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get user login info from backend
 * @param { object } action - Action with payload data
 */
export function* getUserLoginInfo(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { username, isPatient, documentType } = data;

  try {
    const response = yield call(
      userService.getUserLoginInfo,
      username,
      isPatient,
      documentType
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

export function* getUserDataRegister(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { username, isPatient, documentType } = data;

  try {
    const response = yield call(
      userService.getUserDataRegister,
      username,
      isPatient,
      documentType
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get user login info from backend
 * @param { object } action - Action with payload data
 */
export function* getValidatePermission(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const responseRead = yield call(userService.getValidatePermission, {
      ...params,
      permission: 'read'
    });
    const responseSave = yield call(userService.getValidatePermission, {
      ...params,
      permission: 'save'
    });
    const responseUpdate = yield call(userService.getValidatePermission, {
      ...params,
      permission: 'update'
    });
    const responseDelete = yield call(userService.getValidatePermission, {
      ...params,
      permission: 'delete'
    });
    const responseMassive = yield call(userService.getValidatePermission, {
      ...params,
      permission: 'massive'
    });
    yield put(
      setResponse(
        'success',
        false,
        false,
        {
          read: responseRead.data,
          save: responseSave.data,
          update: responseUpdate.data,
          delete: responseDelete.data,
          massive: responseMassive.data
        },
        callback
      )
    );
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to log in user with backend
 * @param { object } action - Action with payload data
 */
export function* setUserInfo(action) {
  const {
    callback,
    payload: { data }
  } = action;

  try {
    yield put(setUser(data));
    yield put(setResponse('success', false, false, false, callback));
  } catch (err) {
    yield put(setResponse('error', false, false, err.message, callback));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get users from backend
 * @param { object } action - Action with payload data
 */
export function* getUsersArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(userService.getUsers, paged, limit, page);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get user info by id
 * @param { object } action - Action with payload data
 */
export function* getUserById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(userService.getUserById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.userNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to get user info by username
 * @param { object } action - Action with payload data
 */
export function* getUserByUserName(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;
  const { username } = data;

  try {
    const response = yield call(userService.getUserByUserName, username);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.userNotFound' || customError.message,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get user info by email
 * @param { object } action - Action with payload data
 */
export function* getUserByEmail(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;
  const { email } = data;

  try {
    const response = yield call(userService.getUserByEmail, email);
    yield put(setUser(response.data.user));
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.userNotFound' || customError.message,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to create user
 * @param { object } action - Action with payload data
 */
export function* createUser(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { name, description } = data;

  try {
    const response = yield call(userService.createUser, name, description);
    yield put(
      setResponse(
        'success',
        'new user',
        'commons.userCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update user
 * @param { object } action - Action with payload data
 */
export function* updateUser(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { name, description, id } = data;

  try {
    const response = yield call(userService.updateUser, name, description, id);
    yield put(
      setResponse(
        'success',
        'updated user',
        'commons.userUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete user by id
 * @param { object } action - Action with payload data
 */
export function* deleteUserById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(userService.deleteUserById, id);
    yield put(
      setResponse(
        'success',
        'deleted user',
        'commons.userDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        err.message || customError.message,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Declare all component sagas
 */
export function* watchUserSaga() {
  yield takeLatest(GET_USER_LOGIN_INFO, getUserLoginInfo);
  yield takeLatest(GET_USER_DATA_REGISTER, getUserDataRegister);
  yield takeLatest(GET_VALIDATE_PERMISSION, getValidatePermission);
  yield takeLatest(SET_USER_INFO, setUserInfo);
  yield takeLatest(GET_ALL_USERS, getUsersArray);
  yield takeLatest(GET_USER_BY_ID, getUserById);
  yield takeLatest(GET_USER_BY_USERNAME, getUserByUserName);
  yield takeLatest(GET_USER_BY_EMAIL, getUserByEmail);
  yield takeLatest(CREATE_USER, createUser);
  yield takeLatest(UPDATE_USER, updateUser);
  yield takeLatest(DELETE_USER_BY_ID, deleteUserById);
}
